import { useState } from 'react';
import { BADGES, Badge, getTierColor, getTierGlow, getTierBackground } from '@/types/badges';
import { cn } from '@/lib/utils';
import { Lock, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface BadgeGridProps {
  isBadgeUnlocked: (badgeId: string) => boolean;
  getBadgeUnlockDate: (badgeId: string) => Date | null;
  isPremium: boolean;
  onBadgeClick?: (badge: Badge) => void;
}

export function BadgeGrid({ 
  isBadgeUnlocked, 
  getBadgeUnlockDate, 
  isPremium,
  onBadgeClick 
}: BadgeGridProps) {
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleBadgeClick = (badge: Badge, unlocked: boolean) => {
    if (unlocked) {
      onBadgeClick?.(badge);
    } else {
      setSelectedBadge(badge);
      setShowInfoModal(true);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-foreground">Badges</h3>
          <span className="text-xs text-muted-foreground">
            {BADGES.filter(b => isBadgeUnlocked(b.id)).length} / {BADGES.length} Unlocked
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {BADGES.map((badge) => {
            const unlocked = isBadgeUnlocked(badge.id);
            const unlockDate = getBadgeUnlockDate(badge.id);
            
            return (
              <button
                key={badge.id}
                onClick={() => handleBadgeClick(badge, unlocked)}
                className={cn(
                  "relative aspect-[4/3] rounded-2xl flex flex-col items-center justify-center p-4 transition-all duration-300 overflow-hidden",
                  unlocked 
                    ? `${getTierBackground(badge.tier)} shadow-lg ${getTierGlow(badge.tier)} shadow-xl ${isPremium ? 'ring-2 ring-white/30' : ''}`
                    : "bg-card/50 border border-border/30 hover:border-primary/30 hover:bg-card/70",
                  "hover:scale-[1.03] cursor-pointer group"
                )}
                title={unlocked ? `${badge.name} - ${unlockDate?.toLocaleDateString()}` : `Click to see how to unlock`}
              >
                {/* Shine effect for unlocked badges */}
                {unlocked && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-white/10" />
                )}
                
                {/* Premium glow pulse */}
                {unlocked && isPremium && (
                  <div className={cn(
                    "absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500",
                    `bg-gradient-to-br ${getTierColor(badge.tier)} blur-xl`
                  )} />
                )}
                
                {/* Badge icon */}
                <div className={cn(
                  "relative z-10 transition-transform duration-300 group-hover:scale-110 text-5xl",
                  unlocked ? "drop-shadow-lg" : "grayscale opacity-40"
                )}>
                  {badge.icon}
                  {!unlocked && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-muted-foreground drop-shadow-md" />
                    </div>
                  )}
                </div>
                
                {/* Badge name */}
                <span className={cn(
                  "relative z-10 text-sm font-semibold text-center leading-tight mt-3 tracking-wide",
                  unlocked ? "text-white drop-shadow-md" : "text-muted-foreground/60"
                )}>
                  {badge.name}
                </span>
              </button>
            );
          })}
        </div>
        
        {!isPremium && (
          <p className="text-xs text-center text-muted-foreground">
            Upgrade to Premium for glowing badge borders ✨
          </p>
        )}
      </div>

      {/* Badge Info Modal for locked badges */}
      <Dialog open={showInfoModal} onOpenChange={setShowInfoModal}>
        <DialogContent className="bg-background/95 backdrop-blur-xl border-white/10 max-w-xs p-0 overflow-hidden rounded-2xl">
          <VisuallyHidden>
            <DialogTitle>Badge Details</DialogTitle>
          </VisuallyHidden>
          
          {selectedBadge && (
            <div className="relative p-6 flex flex-col items-center text-center">
              {/* Background gradient */}
              <div className={cn(
                "absolute inset-0 opacity-10",
                `bg-gradient-to-br ${getTierColor(selectedBadge.tier)}`
              )} />
              
              {/* Close button */}
              <button 
                onClick={() => setShowInfoModal(false)}
                className="absolute top-3 right-3 z-20 p-1.5 rounded-full bg-white/10 text-muted-foreground hover:text-foreground hover:bg-white/20 transition-all"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Badge Icon */}
              <div className={cn(
                "relative w-20 h-20 rounded-full flex items-center justify-center mb-4 bg-muted/30 border-2 border-dashed border-muted-foreground/30"
              )}>
                <span className="text-4xl grayscale opacity-40">{selectedBadge.icon}</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-muted-foreground" />
                </div>
              </div>

              {/* Badge Name */}
              <h2 className="text-lg font-bold text-foreground mb-1">
                {selectedBadge.name}
              </h2>
              
              {/* Tier badge */}
              <span className={cn(
                "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4",
                `bg-gradient-to-r ${getTierColor(selectedBadge.tier)} text-white/90`
              )}>
                {selectedBadge.tier}
              </span>

              {/* How to unlock */}
              <div className="bg-white/5 backdrop-blur rounded-xl p-4 w-full border border-white/10">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">How to unlock</p>
                <p className="text-sm font-medium text-foreground">
                  {selectedBadge.condition}
                </p>
              </div>

              {/* Motivational message */}
              <p className="text-xs text-muted-foreground mt-4 italic">
                Keep grinding — you'll unlock this soon! 💪
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
